//@flow
import {useSelector} from 'react-redux';
import {actions as SubCategoryActions} from '../duck';
import {actions as CategoryActions} from '../../categories/duck';
import {useActions} from '../../../utils/globalStore';
import type {RootState} from '../..';
import {useCallback, useState} from 'react';
import {
  createSubcategoryItemService,
  editSubcategoryItemService,
  deleteSubcategoryItemService,
  sortSubcategoryItemService,
} from '../promises';

import type {CreateSubCategoryItemRequest} from '../promises';

const subCategoriesSelector = (state: RootState) => ({
  subcategoriesFilter: state.subCategories.subcategories || [],
  isFetchingSubcategories: state.subCategories.isFetchingSubcategories,
  paginationSubcategories: state.subCategories.pagination,
});

export function useSubCategories() {
  const subCategories = useSelector(subCategoriesSelector);
  return subCategories;
}

const subCategoriesActions = {
  fetchSubCategories: SubCategoryActions.fetchSubcategories,
  resetSubCategoriesFilter: SubCategoryActions.resetSubcategoriesFilter,
};

export function useSubCategoriesActions(): {
  fetchSubCategories: (payload: Object) => void,
  resetSubCategoriesFilter: () => void,
  searchSubCategories: (name: string) => void,
} {
  const {fetchSubCategories, resetSubCategoriesFilter} = useActions(
    subCategoriesActions
  );

  const searchSubCategories = useCallback(
    (name: string) => {
      fetchSubCategories({
        queryFilter: {
          name,
        },
      });
    },
    [fetchSubCategories]
  );

  return {
    fetchSubCategories,
    resetSubCategoriesFilter,
    searchSubCategories,
  };
}

export function useSubCategoriesItem() {
  const [loading, setLoading] = useState(false);

  const fetchCategories = useActions(CategoryActions.fetchCategories);

  const createSubCategoryItem = useCallback(
    (payload: CreateSubCategoryItemRequest) => {
      setLoading(true);
      return createSubcategoryItemService(payload)
        .then(() => fetchCategories({shouldResetPagination: true}))
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    },
    [fetchCategories]
  );

  const editSubCategoryItem = useCallback(
    (subCategoryItemId: string, name: string) => {
      setLoading(true);
      return editSubcategoryItemService(subCategoryItemId, name)
        .then(() => fetchCategories({shouldResetPagination: true}))
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    },
    [fetchCategories]
  );

  const deleteSubCategoryItem = useCallback(
    (subCategoryItemId: string) => {
      setLoading(true);
      return deleteSubcategoryItemService(subCategoryItemId)
        .then(() => fetchCategories({shouldResetPagination: true}))
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    },
    [fetchCategories]
  );

  const sortSubCategoryItem = useCallback(
    (originId: string, targetId: string, subCategoryId: string) => {
      setLoading(true);
      return sortSubcategoryItemService({
        originId,
        targetId,
        subCategoryId,
      })
        .then(() => fetchCategories({shouldResetPagination: true}))
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    },
    [fetchCategories]
  );

  return {
    createSubCategoryItem,
    editSubCategoryItem,
    deleteSubCategoryItem,
    sortSubCategoryItem,
    loading,
  };
}
